import { annotateFontFallbacks, authoringVisibility, clipPhotoWindow, coverPhotoFrame, flowText, photoWindow, textFitRange, textFlowRegion, usesTextFlow, withScriptFallback } from '@pz/scene';
import { fontIdsIn, fontsInUse, withFontTwins } from './used-fonts.js';
import type { HeadWindow, Scene, SceneElement, TextFlowFont } from '@pz/scene';
import {
  applyShopperPlacements,
  applyTextGlue,
  expandDesignAreas,
  formatDate,
  inSpotifyScope,
  mapCoordinateText,
  normalizeDesignAnswer,
  projectCollageValues,
  readPlacements,
  shopperEffects,
  spotifyPlayerScope,
} from '@pz/scene';
import type { BindingDef, ProductDefinition, Values } from './types.js';

/**
 * The shopper's live preview.
 *
 * Two renderers, chosen at runtime. The real one is the same Skia engine that
 * produces the print file, so what the shopper approves is what gets made — but its
 * WebAssembly is several megabytes, so it is fetched only after the form is usable
 * and never on a device that cannot run it. The fallback draws with the browser's
 * own 2D canvas: close enough to choose a photo and a name by, and it means a weak
 * phone or a blocked WebAssembly still sells the product rather than showing an
 * empty box.
 */

export type RendererKind = 'skia' | 'canvas2d';

export interface PreviewOptions {
  canvas: HTMLCanvasElement;
  product: ProductDefinition;
  onRendererChange?: (kind: RendererKind) => void;
  /** Fired after every completed paint — thumbnails copy the canvas here. */
  onDrawn?: () => void;
}

/**
 * Applies the shopper's answers to a scene, exactly as the server will.
 *
 * Bindings are matched by ELEMENT id, not by which side they were recorded on:
 * the preview scene carries the same elements as the print side under the same
 * ids, and a binding recorded against the print side must drive both — otherwise
 * the live preview sits frozen while the print file changes.
 *
 * A layer a question drives but nobody has answered comes out EMPTY, not
 * showing the design's stand-in — see `blankUnanswered`, mirrored in
 * api/src/resolve.ts.
 */
/** Mirrors api/src/resolve.ts `pictureForLetter`. */
function pictureForLetter(
  pictures: Array<{ id: string; name: string }> | undefined,
  letter: string,
): string | undefined {
  if (!pictures || !letter) return undefined;
  const wanted = letter.toLowerCase();
  const normalise = (name: string): string =>
    name.toLowerCase().replace(/\.[a-z0-9]+$/, '').replace(/^(letter|slovo)[-_ ]?/, '').trim();
  return pictures.find((p) => normalise(p.name) === wanted)?.id;
}

/** Mirrors api/src/resolve.ts `shapeSlotAnswer`. */
function shapeSlotAnswer(
  property: string,
  value: unknown,
  transform: Record<string, unknown> | undefined,
  libraries: Record<string, Array<{ id: string; name: string }>>,
): unknown {
  const libraryId = typeof transform?.libraryId === 'string' ? transform.libraryId : '';
  const index = Number(transform?.index ?? 0) || 0;
  if (property === 'letter') {
    const letters = typeof value === 'string' ? [...value.replace(/\s+/g, '')] : [];
    const letter = letters[index];
    return letter ? (pictureForLetter(libraries[libraryId], letter) ?? null) : null;
  }
  if (property === 'clipartSlot') {
    const picks = Array.isArray(value) ? value : [];
    const pick = picks[index];
    return typeof pick === 'string' && pick ? pick : null;
  }
  if (property === 'starMapToggle') {
    return { key: transform?.key, on: value === true || value === 'true' || value === 'on' || value === '1' };
  }
  return value;
}

const SLOT_PROPERTIES = new Set(['letter', 'clipartSlot', 'starMapToggle']);

/**
 * Which seat at a multi-photo field each bound layer takes — mirrors
 * api/src/resolve.ts `uploadFanOut` exactly, so the slot a photo lands in on
 * the product page is the slot it lands in on the print file.
 *
 * The merchant's own numbered list wins ("Image Placeholder IDs", carried on
 * the field as `config.targetElementIds`); with no list, the option's plain
 * `image` bindings fill in the order they were recorded.
 */
export function uploadFanOut(
  bindings: BindingDef[],
  uploadTargets: Record<string, string[]> = {},
): Map<string, Map<string, number>> {
  const seats = new Map<string, Map<string, number>>();
  const byOption = new Map<string, string[]>();
  for (const binding of bindings) {
    if (binding.property !== 'image' || binding.optionValueId) continue;
    const list = byOption.get(binding.optionId) ?? [];
    if (!list.includes(binding.elementId)) list.push(binding.elementId);
    byOption.set(binding.optionId, list);
  }
  for (const [optionId, bound] of byOption) {
    const declared = uploadTargets[optionId];
    const order =
      Array.isArray(declared) && declared.length > 0
        ? declared.filter((id) => bound.includes(id))
        : bound;
    if (order.length < 2) continue;
    seats.set(optionId, new Map(order.map((id, i) => [id, i])));
  }
  return seats;
}

/**
 * The numbered placeholder lists on a product's photo fields — the Multiple
 * Upload Image fields' and the photo collages' — keyed by field, ready to hand
 * to `applyValues`. The shared copy in `@pz/scene`, the one the commit path
 * uses too, so both sides seat every photo in the same slot.
 */
export { uploadTargetsOf } from '@pz/scene';

/** One seat's share of a multi-photo answer — mirrors api/src/resolve.ts. */
function shapeUploadAnswer(value: unknown, seat: number | undefined): unknown {
  if (!Array.isArray(value)) return value;
  return value[seat ?? 0] ?? null;
}

/**
 * A layer a shopper question drives, with that question still unanswered: it is
 * emptied rather than left showing the design's stand-in.
 *
 * Two stand-ins exist, both meant for the merchant at the drawing board and
 * neither for the shop: a text layer's `default` ("Ana i Marko", "Naziv pesme")
 * and an image slot's `sample` test picture. Shown on the product page they
 * read as the shopper's own words and the shopper's own photo, which is a
 * preview telling a lie — and worse, it hides what the shopper still has to do.
 *
 * So a bound text layer gets a real empty string (`value ?? default` then
 * yields blank, in both renderers), and a bound photo slot loses its `sample`,
 * which is what both the flat preview below and the CanvasKit renderer's
 * `samples` option read. A layer no question drives keeps everything: that is
 * the design's own fixed wording and artwork.
 *
 * Choice-scoped text bindings (`optionValueId`) are left alone — they are
 * waiting their turn rather than driving the layer — as are lines fed from a
 * Spotify pick, which the pick loop below fills or blanks itself.
 *
 * Returns whether it changed anything. Mirrors api/src/resolve.ts, which runs
 * the text half of this rule (the server never draws samples).
 */
function blankUnanswered(
  byId: Map<string, SceneElement>,
  binding: BindingDef,
  element: SceneElement,
): boolean {
  if (
    binding.property === 'text' &&
    element.type === 'text' &&
    !binding.optionValueId &&
    binding.transform?.from !== 'spotify'
  ) {
    byId.set(binding.elementId, { ...element, value: '' });
    return true;
  }
  if (
    binding.property === 'image' &&
    (element.type === 'image' || element.type === 'vector') &&
    element.sample !== undefined
  ) {
    // The key goes, not just its value: `{...el, sample: undefined}` would
    // still be an own property, and the scene the server resolves has none.
    const { sample: _sample, ...rest } = element;
    byId.set(binding.elementId, rest as SceneElement);
    return true;
  }
  return false;
}

export function applyValues(
  scene: Scene,
  bindings: BindingDef[],
  answered: Values,
  /** Option value id -> its stored text, for bindings scoped to one choice. */
  valueLabels: Record<string, string> = {},
  /** Collections slot bindings pick from, keyed by collection id. */
  libraries: Record<string, Array<{ id: string; name: string }>> = {},
  /**
   * Photo fields that fill several slots at once, from each field's
   * `config.targetElementIds` — see `uploadTargetsOf`.
   */
  uploadTargets: Record<string, string[]> = {},
  /**
   * Design layers the EDITOR is hiding right now (not `layer.visible === false`,
   * which is stored in the answer). Only the designer has any; the card and the
   * product page pass none, so this preview shows the design as the order will.
   */
  hiddenLayerIds?: ReadonlySet<string> | readonly string[],
): Scene {
  const seats = uploadFanOut(bindings, uploadTargets);
  // A photo collage's answer becomes the per-slot list the fan-out prints —
  // the same one line as api/src/resolve.ts `resolveScenes`.
  const values = projectCollageValues(answered, seats);
  const byId = new Map(scene.elements.map((el) => [el.id, el] as const));
  let changed = false;
  // Find these before applying anything. Imported players can carry both an
  // explicit Spotify transform and the plain text binding generated for the
  // editable title/artist line. Binding order is storage order, not precedence:
  // the shopper's wording must win even when the old Spotify binding comes
  // after it. Mirrors api/src/resolve.ts.
  const retyped = new Set<string>();
  const spotifyFallback = new Set<string>();
  for (const binding of bindings) {
    const element = byId.get(binding.elementId);
    const value = values[binding.optionId];
    if (
      binding.property === 'text' &&
      binding.transform?.from === 'spotify' &&
      value !== undefined &&
      value !== null &&
      value !== ''
    ) {
      spotifyFallback.add(binding.elementId);
    }
    if (
      binding.property === 'text' &&
      element?.type === 'text' &&
      element.spotifyRole &&
      binding.transform?.from !== 'spotify' &&
      (!binding.optionValueId || matchesChoice(value, binding.optionValueId, valueLabels)) &&
      typeof value === 'string' &&
      value.trim() !== ''
    ) {
      retyped.add(binding.elementId);
    }
  }
  for (const binding of bindings) {
    const element = byId.get(binding.elementId);
    if (!element) continue;
    const condition=binding.property==='visibility' ? authoringVisibility(binding.transform,values) : undefined;
    if(condition!==undefined) {
      // A layer the mockup hides stays hidden there, whatever the form answers.
      if(!element.mockupHidden) byId.set(binding.elementId,applyProperty(element,'visibility',condition));
      changed=true;
      continue;
    }
    const value = values[binding.optionId];
    // Slot bindings run even unanswered — mirrors the server.
    if (SLOT_PROPERTIES.has(binding.property)) {
      byId.set(
        binding.elementId,
        applyProperty(element, binding.property, shapeSlotAnswer(binding.property, value, binding.transform, libraries)),
      );
      changed = true;
      continue;
    }
    // Nothing answered yet — and on the shop that means the layer shows
    // NOTHING. A design's `default` text and `sample` picture exist for the
    // merchant drawing it; a shopper must never be shown "Ana i Marko" or a
    // stranger's family photo as if it were their product, and the print file
    // has to agree, so the same blanking runs on the server
    // (api/src/resolve.ts — text only there, since it never draws samples).
    if (value === undefined || value === null || value === '') {
      if (
        binding.property === 'text' &&
        element.type === 'text' &&
        element.spotifyRole &&
        binding.transform?.from !== 'spotify' &&
        spotifyFallback.has(binding.elementId)
      ) {
        continue;
      }
      if (blankUnanswered(byId, binding, element)) changed = true;
      continue;
    }

    // A migrated Spotify transform is only the automatic fallback. Once the
    // same role line has a manual answer, applying it would make the live
    // preview depend on the arbitrary order of the two binding rows.
    if (
      binding.property === 'text' &&
      retyped.has(binding.elementId) &&
      binding.transform?.from === 'spotify'
    ) {
      continue;
    }
    if (
      binding.property === 'text' &&
      element.type === 'text' &&
      element.spotifyRole &&
      binding.transform?.from !== 'spotify' &&
      typeof value === 'string' &&
      value.trim() === ''
    ) {
      continue;
    }

    // Mirrors the server exactly (see api/src/resolve.ts). A layout swatch shows
    // the group it selects and hides the others; anything else scoped to a choice
    // simply waits its turn.
    if (binding.optionValueId) {
      const selected = matchesChoice(value, binding.optionValueId, valueLabels);
      if (binding.property === 'visibility') {
        byId.set(binding.elementId, applyProperty(element, 'visibility', selected));
        changed = true;
        continue;
      }
      if (!selected) continue;

      // A choice that carries its own artwork — see api/src/resolve.ts.
      const carried =
        typeof binding.transform?.assetId === 'string'
          ? binding.transform.assetId
          : typeof binding.transform?.color === 'string'
            ? binding.transform.color
            : undefined;
      if (carried !== undefined) {
        byId.set(binding.elementId, applyProperty(element, binding.property, carried));
        changed = true;
        continue;
      }
    }

    // A multi-photo answer is shared out across the slots it was bound to, in
    // the merchant's order — mirrors api/src/resolve.ts.
    const shared =
      binding.property === 'image'
        ? shapeUploadAnswer(value, seats.get(binding.optionId)?.get(binding.elementId))
        : value;
    let next = applyProperty(element, binding.property, extract(shared, binding.transform));
    // An empty seat of a multi-photo answer — a collage slot with no photo
    // yet — draws nothing, never the author's `sample` (the `blankUnanswered`
    // rule; the server never draws samples, so this is parity).
    if (
      binding.property === 'image' &&
      Array.isArray(value) &&
      shared == null &&
      (next.type === 'image' || next.type === 'vector') &&
      next.sample !== undefined
    ) {
      const { sample: _sample, ...rest } = next;
      next = rest as SceneElement;
    }
    byId.set(binding.elementId, next);
    changed = true;
  }

  // Layers marked with a Spotify role fill themselves from the picked track —
  // mirrors api/src/resolve.ts, so the live preview shows the same plaque the
  // print file will carry.
  //
  // A pick whose bound element sits in a folder flagged as a Spotify player
  // fills only that folder, so two players on one design stay independent;
  // anything else falls back to the whole scene.
  const picks: Array<{ track: PickedTrack; elementId: string }> = [];
  for (const binding of bindings) {
    if (binding.property !== 'spotifyUri') continue;
    const track = asTrack(values[binding.optionId]);
    if (track) picks.push({ track, elementId: binding.elementId });
  }
  const filledByPick = new Set<string>();
  for (const pick of picks) {
    const scope = spotifyPlayerScope(scene, byId.get(pick.elementId));
    for (const [id, el] of byId) {
      if (filledByPick.has(id) || retyped.has(id) || !inSpotifyScope(scope, el)) continue;
      if (el.type === 'text' && el.spotifyRole) {
        const text = trackField(pick.track, el.spotifyRole);
        if (text !== null) {
          byId.set(id, { ...el, value: text });
          filledByPick.add(id);
          changed = true;
        }
      } else if (el.type === 'image' && el.spotifyRole === 'albumArt' && el.value == null) {
        const art = trackField(pick.track, 'albumArt');
        if (art) {
          byId.set(id, { ...el, value: art });
          filledByPick.add(id);
          changed = true;
        }
      }
    }
  }

  // A text line linked to a map prints that map's coordinates — mirrors
  // api/src/resolve.ts exactly, so the numbers on the product page are the
  // numbers on the poster. After every binding, so a place the shopper just
  // searched for has already moved the map.
  for (const [id, el] of byId) {
    if (el.type !== 'text' || !el.mapCoordinates) continue;
    const coordinates = mapCoordinateText(byId.values(), el);
    if (coordinates === null || el.value === coordinates) continue;
    byId.set(id, { ...el, value: coordinates });
    changed = true;
  }

  // Where the shopper dragged a movable layer — mirrors api/src/resolve.ts.
  const placed = applyShopperPlacements(
    changed ? { ...scene, elements: scene.elements.map((el) => byId.get(el.id) ?? el) } : scene,
    readPlacements(values),
  );
  // And the design zones become native layers last — mirrors api/src/resolve.ts
  // exactly, so the design the shopper is looking at while they build it is the
  // one the print file will carry. Idempotent on a scene with no zone.
  return expandDesignAreas(placed, {
    target: placed.target === 'preview' ? 'preview' : 'print',
    ...(hiddenLayerIds ? { hiddenLayerIds } : {}),
  });
}

/** Whether the shopper's answer selects a particular choice. */
function matchesChoice(
  answer: unknown,
  optionValueId: string,
  valueLabels: Record<string, string>,
): boolean {
  const wanted = valueLabels[optionValueId];
  const answers = Array.isArray(answer) ? answer : [answer];
  return answers.some((one) => {
    const text = one == null ? '' : String(one);
    return text === optionValueId || (wanted !== undefined && text === wanted);
  });
}

/** What the song picker submits; mirrors api/src/resolve.ts. */
interface PickedTrack {
  uri?: unknown;
  name?: unknown;
  artists?: unknown;
  artist?: unknown;
  album?: unknown;
  albumArt?: unknown;
  year?: unknown;
  durationMs?: unknown;
}

/** Reads an answer as a picked track, when it is one. */
function asTrack(value: unknown): PickedTrack | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const track = value as PickedTrack;
  return typeof track.uri === 'string' && track.uri.startsWith('spotify:') ? track : undefined;
}

/** One field of a picked track, as the text or asset id a layer wants. */
function trackField(track: PickedTrack, field: unknown): string | null {
  switch (field) {
    case 'name':
    case 'title':
      return track.name == null ? null : String(track.name);
    case 'artists':
    case 'artist': {
      // Both names mean EVERY credited artist, joined with ", " — mirrors
      // api/src/resolve.ts. `artist` stays as an alias for migrated bindings.
      const all = track.artists;
      if (Array.isArray(all)) return all.join(', ');
      if (all != null && String(all) !== '') return String(all);
      return track.artist == null ? null : String(track.artist);
    }
    case 'album':
      return track.album == null ? null : String(track.album);
    case 'year':
      return track.year == null ? null : String(track.year);
    // "4:23" — the same words api/src/resolve.ts prints.
    case 'duration': {
      const total = Math.round(Number(track.durationMs) / 1000);
      return Number.isFinite(total) && total > 0
        ? `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
        : null;
    }
    case 'albumArt':
      return track.albumArt == null ? null : String(track.albumArt);
    default:
      return null;
  }
}

/** Pulls the field a binding asked for out of a compound answer. */
function extract(value: unknown, transform: Record<string, unknown> | undefined): unknown {
  if (!transform || transform.from !== 'spotify') return value;
  if (!value || typeof value !== 'object') return value;
  if (transform.field === undefined) return value;
  return trackField(value as PickedTrack, transform.field);
}

function applyProperty(element: SceneElement, property: string, value: unknown): SceneElement {
  switch (property) {
    // Text and date are two cases, not one, because the server's are two.
    // Merged, the preview applied the UNION of both target lists to both
    // properties: a text-bound answer landed in a calendar's `value` (where it
    // fails the date regex and silently reverts to today) and a date landed in
    // a crossword's word list. The server no-ops both — so the shopper's
    // preview and the print file disagreed, which is the one thing a preview
    // may never do.
    case 'text':
      // Text drives text and the word-driven puzzles: their value IS the words
      // the shopper typed.
      return element.type === 'text' ||
        element.type === 'crossword' ||
        element.type === 'wordSearch' ||
        element.type === 'wordCloud'
        ? ({ ...element, value: value == null ? null : String(value) } as SceneElement)
        : element;

    case 'date': {
      // A date drives a text layer (formatted print), a calendar's highlighted
      // day, which moon is drawn, or a star map's night.
      if (
        element.type !== 'text' &&
        element.type !== 'calendar' &&
        element.type !== 'moonPhase' &&
        element.type !== 'starMap'
      ) {
        return element;
      }
      if (value == null) return { ...element, value: null } as SceneElement;
      // Mirrors the server's date formatting: a text layer with a Date Format
      // shows the day the merchant chose, a calendar the raw date. The shopper
      // must not see one string here and another on the print file.
      const raw = String(value);
      const text =
        element.type === 'text' && element.dateFormat ? formatDate(raw, element.dateFormat) : raw;
      return { ...element, value: text } as SceneElement;
    }
    /**
     * The whole design a shopper built inside a print zone — mirrors
     * api/src/resolve.ts, including the normalisation: the widget draws the
     * NORMALISED answer, so a value the server will clamp is already clamped on
     * screen rather than looking right here and shifting at commit.
     */
    case 'design': {
      if (element.type !== 'designArea') return element;
      return { ...element, value: normalizeDesignAnswer(value) ?? null };
    }
    case 'image': {
      // Customily's Map option binds through its image slot, so every migrated
      // map product carries `image → map element`. The answer is a place, not a
      // picture — route it to the location handler or the map never moves.
      if (element.type === 'map' || element.type === 'starMap') {
        return applyProperty(element, 'location', value);
      }
      // A vector placeholder takes a picked graphic exactly as a photo slot
      // takes a photo — Customily's "Dynamic Vector" swaps the whole artwork,
      // and both kinds of layer resolve their picture from `value` first.
      // Mirrors the server, which has always accepted both.
      if (element.type !== 'image' && element.type !== 'vector') return element;
      // Mirrors the server: either a plain asset id, or {assetId, crop} carrying
      // the shopper's framing.
      if (value && typeof value === 'object' && 'assetId' in (value as object)) {
        const framed = value as {
          assetId: unknown;
          crop?: { cx: number; cy: number; zoom: number };
          filter?: unknown;
          keepOriginal?: boolean;
        };
        return {
          ...element,
          value: framed.assetId == null ? null : String(framed.assetId),
          // Framing and looks belong to a photo; a vector has neither.
          ...(element.type === 'image' && framed.crop ? { crop: framed.crop } : {}),
          // The look, and "use my photo as it is". Composed by the same function
          // the server's resolver calls, so what is previewed is what is printed.
          ...(element.type === 'image' && ('filter' in framed || framed.keepOriginal)
            ? { effects: shopperEffects(element.effects, framed) }
            : {}),
        };
      }
      // Null clears the slot: an unfilled seat of a multi-photo field must show
      // nothing, not the string "null".
      return { ...element, value: value == null ? null : String(value) };
    }
    case 'qrUrl':
      return element.type === 'qr' ? { ...element, value: String(value) } : element;
    case 'spotifyUri': {
      if (element.type !== 'spotifyCode') return element;
      const uri =
        typeof value === 'string'
          ? value
          : value && typeof value === 'object' && 'uri' in value
            ? String((value as { uri: unknown }).uri)
            : null;
      return { ...element, value: uri && uri.startsWith('spotify:') ? uri : null };
    }
    case 'color': {
      const hex = String(value);
      if (element.type === 'text') return { ...element, color: { ...element.color, hex } };
      if (element.type === 'rect') return { ...element, fill: { ...element.fill, hex } };
      // Mirrors the server: a colour on artwork is a tint.
      if (element.type === 'image') return { ...element, colorize: { hex, alpha: 1 } };
      if (element.type === 'vector') return { ...element, singleColor: { hex, alpha: 1 } };
      return element;
    }
    // Customily's "Image Color" and "Dynamic Vector Color" — the same operation
    // as `color` on the same layer, under the function names the importer
    // carries across. Mirrors api/src/resolve.ts.
    case 'imageColor': {
      return element.type === 'image' ? applyProperty(element, 'color', value) : element;
    }
    case 'vectorColor': {
      return element.type === 'vector' ? applyProperty(element, 'color', value) : element;
    }
    case 'mask': {
      const assetId = typeof value === 'string' ? value : (value as { assetId?: unknown } | null)?.assetId;
      if (typeof assetId !== 'string' || !assetId) return element;
      return { ...element, mask: { ...(element.mask ?? {}), assetId } } as SceneElement;
    }
    case 'outlineColor': {
      const hex = typeof value === 'string' ? value : '';
      if (!hex || element.type !== 'text') return element;
      return {
        ...element,
        outline: { width: element.outline?.width ?? 2, color: { ...(element.outline?.color ?? {}), hex, alpha: 1 } },
      };
    }
    case 'font':
      return element.type === 'text'
        ? { ...element, font: { ...element.font, fontId: String(value) } }
        : element;
    case 'visibility':
      return { ...element, visible: Boolean(value) };
    case 'mapStyle': {
      // A style swatch repaints the map without moving it; the shopper's chosen
      // place stays in the element's value. Mirrors the server exactly — this
      // branch was missing here once, and the style showed up only in print.
      if (element.type !== 'map') return element;
      const name = typeof value === 'string' ? value.trim() : '';
      if (!name) return element;
      return { ...element, config: { ...element.config, style: name } } as SceneElement;
    }
    case 'starMapStyle': {
      if (element.type !== 'starMap') return element;
      const name = typeof value === 'string' ? value.trim().toLowerCase() : '';
      if (!name) return element;
      return { ...element, config: { ...element.config, style: name } } as SceneElement;
    }
    /**
     * Which crossword style is showing.
     *
     * Customily's crossword "Styles" list has two modes: alternate between them
     * (a look the merchant fixes) or offer them as options — and an option is a
     * swatch bound here. The value is a style name or its index; the renderer
     * resolves either, so a swatch built from names and one built from
     * positions both work.
     */
    /**
     * The shopper's own words for an AI-painted layer — Customily's "Prompt"
     * function on a Text Input.
     *
     * The prompt lives on the IMAGE element (its function's field is labelled
     * "Image Id", not "Prompt Id"), so the answer replaces the prompt of that
     * layer's AI style rather than being stored on the option. A layer with no
     * AI style yet gains one: "Text to Image" is a style with an empty prompt
     * waiting for exactly this.
     */
    case 'prompt': {
      if (element.type !== 'image') return element;
      const prompt = value == null ? '' : String(value);
      if (!prompt) return element;
      const effects = element.effects ?? [];
      const replaced = effects.map((effect) =>
        effect.kind === 'aiStyle' ? { ...effect, prompt } : effect,
      );
      return {
        ...element,
        effects: replaced.some((effect) => effect.kind === 'aiStyle')
          ? replaced
          : [...effects, { kind: 'aiStyle' as const, prompt }],
      } as SceneElement;
    }
    case 'crosswordStyle': {
      if (element.type !== 'crossword') return element;
      const name = typeof value === 'string' ? value.trim() : typeof value === 'number' ? value : '';
      if (name === '') return element;
      return { ...element, config: { ...element.config, activeStyle: name } } as SceneElement;
    }
    case 'starMapToggle': {
      if (element.type !== 'starMap') return element;
      const toggle = value as { key?: unknown; on?: unknown } | null;
      const key = typeof toggle?.key === 'string' ? toggle.key : '';
      if (!['constellations', 'graticule', 'milkyWay'].includes(key)) return element;
      return { ...element, config: { ...element.config, [key]: Boolean(toggle?.on) } } as SceneElement;
    }
    case 'letter':
    case 'clipartSlot': {
      if (element.type !== 'image') return element;
      const assetId = typeof value === 'string' && value ? value : null;
      return assetId
        ? { ...element, value: assetId, visible: true }
        : { ...element, value: null, visible: false };
    }
    case 'location': {
      const loc = value as { lat?: unknown; lng?: unknown; zoom?: unknown } | null;
      if (!loc || typeof loc !== 'object') return element;
      const lat = Number(loc.lat);
      const lng = Number(loc.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return element;
      // The shopper's own framing of the map: zoom rides along when they
      // adjusted it, exactly like a photo's crop rides with its asset id.
      const zoom = Number(loc.zoom);
      const place = Number.isFinite(zoom) ? { lat, lng, zoom } : { lat, lng };
      if (element.type === 'map') {
        return { ...element, value: place } as SceneElement;
      }
      if (element.type === 'starMap') {
        // A star map's value is its date; the sky's viewpoint lives in config.
        return { ...element, config: { ...element.config, lat, lng } } as SceneElement;
      }
      return element;
    }
    default:
      return element;
  }
}

/** The renderer bundle's public surface, once its script has loaded. */
interface RenderRequest {
  scene: Scene;
  scale: number;
  fonts: Array<{ id: string; url: string }>;
  assets: Array<{ id: string; url: string }>;
  mapboxToken?: string;
  /** The cart picture: no test pictures, and it fails rather than miss a photo (see render-entry). */
  strict?: boolean;
}

interface RenderBundle {
  render(request: RenderRequest): Promise<Uint8Array>;
  /** The visible window of a face cut-out slot. Present since the head-placement release. */
  /** The visible window of a head slot, with its outline (`HeadWindow`); an older bundle omits the outline. */
  headWindow?(request: RenderRequest & { elementId: string }): Promise<HeadWindow | undefined>;
  /** Straight to RGBA — skips the PNG round trip. Present since 0.6.x. */
  renderRaw?(request: RenderRequest): Promise<{ pixels: Uint8Array; width: number; height: number; retryInMs?: number }>;
}

/**
 * Loads the renderer script once.
 *
 * A script tag rather than an import, so the bundler leaves it alone and the
 * several megabytes of WebAssembly behind it are only fetched when a preview is
 * actually drawn.
 */
/**
 * The width to render the preview at, in CSS pixels.
 *
 * The CSS container owns the display size. A raster may be half-resolution
 * during edits or twice as dense on a high-DPI display; neither is a layout
 * measurement. Lightbox dimensions come from previewFrame and the scene.
 */
function displayWidth(canvas: HTMLCanvasElement, scene: Scene): number {
  // CSS controls presentation. Never feed an intrinsic canvas width (or a
  // hover transform) back into raster sizing: DPR/draft switches amplify it.
  const parentWidth = canvas.parentElement?.clientWidth || 0;
  let width = parentWidth || MIN_PREVIEW_WIDTH;
  // The wizard limits portrait previews by CSS height. Honour that cap without
  // measuring the intrinsic raster or a transformed canvas rectangle.
  if (typeof getComputedStyle === 'function') {
    const limit = getComputedStyle(canvas).maxHeight;
    if (/^[0-9.]+px$/.test(limit) && scene.height > 0) width = Math.min(width, Number.parseFloat(limit) * scene.width / scene.height);
  }
  return Math.max(1, Math.round(width));
}

/** Below this the preview reads as broken rather than as small. */
const MIN_PREVIEW_WIDTH = 320;

/**
 * Decodes a PNG the renderer produced.
 *
 * `createImageBitmap` is the fast path and is missing (or refuses a Blob) on
 * older Safari and some Android WebViews; there the preview would simply never
 * appear, so an `<img>` off an object URL stands in.
 */
async function decodeBlob(
  blob: Blob,
): Promise<{ image: CanvasImageSource; width: number; height: number; release: () => void } | null> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      return {
        image: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close(),
      };
    } catch {
      /* fall through to the <img> route */
    }
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      resolve({
        image,
        width: image.naturalWidth,
        height: image.naturalHeight,
        release: () => URL.revokeObjectURL(url),
      });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    image.src = url;
  });
}

/**
 * The pictures the flat fallback draws for a cart snapshot (samples already
 * stripped): the background and each shown image slot's picture, in the order
 * `drawCanvas2d` resolves them.
 */
function snapshotPictures(scene: Scene): string[] {
  const ids = new Set<string>();
  if (scene.background?.assetId) ids.add(scene.background.assetId);
  for (const el of scene.elements) {
    if (el.type !== 'image' || el.visible === false || el.printOnly) continue;
    const id = el.value ??
      (el.source.kind === 'static' ? el.source.assetId : undefined) ??
      (el.source.kind === 'choices' ? el.source.assetIds[0] : undefined);
    if (id) ids.add(id);
  }
  return [...ids];
}

/**
 * The cart picture as a JPEG on white. A 900px PNG of a photo product is half
 * a megabyte or more, uploaded for every design saved ahead of the click and
 * shown in the cart at 76px — on a phone, seconds of upload and a blank square
 * while the cart page loads. The cart shows it on white anyway. The PNG stays
 * when the browser cannot re-encode, or when the JPEG would not be smaller.
 */
async function asJpeg(png: Blob): Promise<Blob> {
  try {
    const picture = await decodeBlob(png);
    if (!picture) return png;
    const canvas = document.createElement('canvas');
    canvas.width = picture.width;
    canvas.height = picture.height;
    const context = canvas.getContext('2d');
    if (!context) {
      picture.release();
      return png;
    }
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(picture.image, 0, 0);
    picture.release();
    const jpeg = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    return jpeg && jpeg.size > 0 && jpeg.size < png.size ? jpeg : png;
  } catch {
    return png;
  }
}

function loadRenderBundle(url: string): Promise<RenderBundle> {
  const existing = (window as unknown as { PersonalizyRender?: RenderBundle }).PersonalizyRender;
  if (existing) return Promise.resolve(existing);

  const once = (src: string): Promise<RenderBundle> => new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      const bundle = (window as unknown as { PersonalizyRender?: RenderBundle }).PersonalizyRender;
      if (bundle) resolve(bundle);
      else reject(new Error('renderer did not register'));
    };
    script.onerror = () => reject(new Error('renderer script failed to load'));
    document.head.appendChild(script);
  });
  // Several megabytes over a phone's connection: a dropped download is tried
  // twice more (the page keeps drawing with the flat preview meanwhile).
  return (async () => {
    let last: unknown;
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 2000 * attempt));
      try {
        return await once(attempt === 0 ? url : `${url}${url.includes('?') ? '&' : '?'}pzretry=${attempt}`);
      } catch (error) {
        last = error;
      }
    }
    throw last;
  })();
}

export class Preview {
  private renderer: RendererKind = 'canvas2d';
  private skia: RenderBundle | undefined;
  private assetUrls = new Map<string, string>();
  private images = new Map<string, HTMLImageElement>();
  private fontFamilies = new Map<string, string>();
  private pending: { scene: Scene; low: boolean } | undefined;
  /** The most recent scene, for repaints triggered by late-arriving fonts or WASM. */
  private lastScene: Scene | undefined;
  /** Resolved scene actually painted, including visibility and shopper placement. */
  paintedScene: Scene | undefined;
  private drawing = false;
  /** When the last draw was requested — rapid succession switches to draft quality. */
  private lastRequestAt = 0;
  private hqTimer: number | undefined;
  /** A repaint for a picture that failed to load and is about to be asked for again. */
  private retryTimer: number | undefined;
  private readonly fontsReady: Promise<void>;
  /** Fonts already asked of the browser, loaded or loading. */
  private readonly fontsAsked = new Set<string>();
  private readonly knownFonts: ReadonlySet<string>;

  constructor(private readonly options: PreviewOptions) {
    for (const asset of options.product.assets) this.assetUrls.set(asset.id, asset.url);
    // Registered under an alias per id, not the declared family: two records of
    // one family (Regular and Bold) would otherwise share a FontFace name and the
    // browser would pick one face for both.
    for (const font of options.product.fonts) this.fontFamilies.set(font.id, `pz-font-${font.id}`);
    this.knownFonts = new Set(options.product.fonts.map((font) => font.id));
    // Only what this product draws with (used-fonts.ts); any other font loads
    // when a scene first names it (enqueue).
    this.fontsReady = this.loadFonts(fontsInUse(options.product));
  }

  /** Render the exact submitted answers, independent of any pending screen paint. */
  /**
   * The cart picture. Drawn as the order will be made — never a design's test
   * picture — and never without a photo the shopper put in: a picture that
   * does not load is asked for again, and the snapshot fails (the caller then
   * leaves the cart to the server's own preview) rather than hand the cart a
   * design with a hole in it.
   */
  async snapshot(scene: Scene): Promise<Blob> {
    const frozen = structuredClone(this.withTwins(scene));
    if (this.skia) {
      const png = await this.skia.render({
        scene: frozen,
        scale: Math.min(1, 900 / Math.max(frozen.width, frozen.height)),
        fonts: this.options.product.fonts.map(f => ({ id: f.id, url: f.url })),
        assets: [...this.assetUrls].map(([id, url]) => ({ id, url })),
        mapboxToken: this.options.product.mapboxToken,
        strict: true,
      });
      return asJpeg(new Blob([new Uint8Array(png).buffer], { type: 'image/png' }));
    }
    await this.fontsReady;
    const canvas = document.createElement('canvas');
    const copy = new Preview({ canvas, product: { ...this.options.product, fonts: [] } });
    copy.assetUrls = new Map(this.assetUrls);
    copy.fontFamilies = new Map(this.fontFamilies);
    frozen.elements = frozen.elements.map((el) => {
      if (!('sample' in el)) return el;
      const { sample: _sample, ...rest } = el as SceneElement & { sample?: unknown };
      return rest as SceneElement;
    });
    for (const id of snapshotPictures(frozen)) {
      let loaded = false;
      for (let attempt = 0; attempt < 3 && !loaded; attempt++) {
        if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, attempt === 1 ? 800 : 2500));
        loaded = (await copy.image(id)) !== undefined;
      }
      if (!loaded) throw new Error(`picture ${id} did not load`);
    }
    await copy.drawCanvas2d(frozen, 900);
    const png = await new Promise<Blob>((resolve, reject) => canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('Preview could not be saved. Please try again.')),
      'image/png',
    ));
    return asJpeg(png);
  }

  /**
   * Makes a freshly uploaded asset drawable.
   *
   * The product definition only lists the template's own assets; the shopper's
   * photo arrives later, and without this registration the renderer would have an
   * asset id with no bytes behind it — a slot that never fills in.
   */
  registerAsset(id: string, url: string): void {
    const changed = this.assetUrls.get(id) !== url;
    if (changed) this.pictureVersion++;
    this.assetUrls.set(id, url);
    this.images.delete(id);
    // AI styling and cut-outs replace pixels without changing the answers.
    // Repaint the latest scene just as we do for a late font or renderer.
    if (changed && this.lastScene) void this.enqueue(this.lastScene, false);
  }

  /**
   * Bumped whenever a picture's pixels change without an answer changing — an
   * upload landing, its cut-out replacing it. The cart picture saved for a
   * design is only good for the pixels it was drawn from.
   */
  pictureVersion = 0;

  /**
   * Measures the part of a face cut-out slot a head will show through, with
   * the print renderer (see `measureHeadWindow`). Waits briefly for the
   * renderer to arrive; undefined — fit to the whole slot — when it cannot.
   * Cached per scene state, since the body behind a head rarely changes.
   */
  async headWindow(scene: Scene, elementId: string): Promise<HeadWindow | undefined> {
    for (let waited = 0; !this.skia && waited < 6000; waited += 200) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    const bundle = this.skia;
    if (!bundle?.headWindow) return undefined;
    // Only what shapes the window matters for the cache: layer geometry,
    // masks and which layers are showing — not the shopper's words.
    const key = `${elementId}|${JSON.stringify(scene.elements.map((el) => [el.id, el.z, el.visible, el.mask?.assetId, el.transform, el.type === 'image' && el.id !== elementId ? (el.value ?? (el.source.kind === 'static' ? el.source.assetId : '')) : '']))}`;
    const known = this.headWindows.get(key);
    if (known) return known;
    const measuring = bundle.headWindow({
      scene: structuredClone(scene),
      elementId,
      scale: 1,
      fonts: this.options.product.fonts.map((f) => ({ id: f.id, url: f.url })),
      assets: [...this.assetUrls].map(([id, url]) => ({ id, url })),
      mapboxToken: this.options.product.mapboxToken,
    }).catch(() => undefined);
    this.headWindows.set(key, measuring);
    return measuring;
  }

  private readonly headWindows = new Map<string, Promise<HeadWindow | undefined>>();

  /** Native, unmirrored artwork pixels for the optional product surface engine. */
  async surfacePixels(scene: Scene): Promise<{ pixels: Uint8Array; width: number; height: number }> {
    if (!this.skia?.renderRaw) throw new Error('The native renderer is unavailable; use the flat preview.');
    return this.skia.renderRaw({
      scene: this.withTwins(scene), scale: Math.min(1, 1024 / Math.max(scene.width, scene.height)),
      fonts: this.options.product.fonts.map(f => ({ id: f.id, url: f.url })),
      assets: [...this.assetUrls].map(([id, url]) => ({ id, url })), mapboxToken: this.options.product.mapboxToken,
    });
  }

  /**
   * Renders a partial scene (transparent where it has nothing) at the preview's
   * own sharpness, beside the preview rather than into it — the on-canvas
   * editor lays the layers above a photo over the photo being dragged.
   * Undefined without the print renderer: the flat fallback cannot do it.
   */
  async renderLayers(scene: Scene): Promise<{ pixels: Uint8Array; width: number; height: number } | undefined> {
    if (!this.skia?.renderRaw) return undefined;
    const canvas = this.options.canvas;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const scale = Math.min(1, (displayWidth(canvas, scene) * dpr) / scene.width);
    return this.skia.renderRaw({
      scene: this.withTwins(scene),
      scale,
      fonts: this.options.product.fonts.map((f) => ({ id: f.id, url: f.url })),
      assets: [...this.assetUrls].map(([id, url]) => ({ id, url })),
      mapboxToken: this.options.product.mapboxToken,
    });
  }

  /** Where an asset's pixels live — the on-canvas editor needs the real image. */
  assetUrl(id: string): string | undefined {
    return this.assetUrls.get(id);
  }

  /**
   * Registers the real font files with the browser, so the fallback renderer draws
   * the same typefaces the print file will use.
   */
  private async loadFonts(ids: ReadonlySet<string>): Promise<void> {
    if (typeof FontFace === 'undefined') return;
    const wanted = this.options.product.fonts.filter((font) => ids.has(font.id) && !this.fontsAsked.has(font.id));
    if (wanted.length === 0) return;
    for (const font of wanted) this.fontsAsked.add(font.id);
    await Promise.all(
      wanted.map(async (font) => {
        try {
          const face = new FontFace(
            this.fontFamilies.get(font.id) ?? font.family,
            `url(${JSON.stringify(font.url)})`,
          );
          await face.load();
          document.fonts.add(face);
        } catch {
          // A font that will not load is not worth failing the preview over.
        }
      }),
    );
    if (this.lastScene) void this.enqueue(this.lastScene, false);
  }

  /**
   * Upgrades to the print renderer in the background.
   *
   * Failure is expected on some devices and is not reported to the shopper: the
   * fallback is already drawing something usable.
   */
  upgrade(rendererUrl: string): Promise<void> {
    if (this.skia) return Promise.resolve();
    // Asked from several places (the shopper starting, the cart picture, the
    // collage board): one download, every caller waits for the same one. A
    // failed one may be tried again by a later call.
    this.upgrading ??= (async () => {
      try {
        this.skia = await loadRenderBundle(rendererUrl);
        this.renderer = 'skia';
        this.options.onRendererChange?.('skia');
        // The 2D approximation is on screen; replace it with the real thing.
        if (this.lastScene) await this.enqueue(this.lastScene, false);
      } catch {
        this.renderer = 'canvas2d';
        this.upgrading = undefined;
      }
    })();
    return this.upgrading;
  }
  private upgrading: Promise<void> | undefined;

  get kind(): RendererKind {
    return this.renderer;
  }

  /**
   * Draws a scene, coalescing rapid changes into the latest one.
   *
   * Quality adapts to the pace of change: a lone change (a swatch click)
   * renders crisp immediately, but a burst — typing, dragging — renders a
   * quarter-resolution draft, which is what keeps the CPU meter calm while the
   * shopper works. One full-quality pass follows automatically once the burst
   * has been quiet for a moment.
   */
  /**
   * Marks each text layer with its font's Serbian twin (definition
   * `fontTwins`), after the answers were applied — so a font the shopper
   * picked, or a layer they added in a design area, is covered too. The
   * renderer switches only when the typed text needs it; the server does the
   * same at commit, so the cart and the print agree.
   */
  private withTwins(scene: Scene): Scene {
    const twins = this.options.product.fontTwins;
    return twins ? annotateFontFallbacks(scene, twins) : scene;
  }

  async draw(sceneIn: Scene): Promise<void> {
    const scene = this.withTwins(sceneIn);
    const now = Date.now();
    const low = this.skia !== undefined && now - this.lastRequestAt < 700;
    this.lastRequestAt = now;
    window.clearTimeout(this.hqTimer);
    if (low) {
      this.hqTimer = window.setTimeout(() => {
        // The burst settled; repaint the same state crisp.
        this.lastRequestAt = 0;
        void this.draw(scene);
      }, 700);
    }
    return this.enqueue(scene, low);
  }

  private async enqueue(scene: Scene, low: boolean): Promise<void> {
    this.lastScene = scene;
    // A font this scene names that is not loaded yet (a font choice picked):
    // loaded now, and the scene drawn again once it is in.
    if (typeof FontFace !== 'undefined' && this.options.product.fonts.length > this.fontsAsked.size) {
      const named = withFontTwins(fontIdsIn(scene.elements, this.knownFonts), this.options.product.fontTwins);
      if ([...named].some((id) => !this.fontsAsked.has(id))) void this.loadFonts(named);
    }
    this.pending = { scene, low };
    if (this.drawing) return;
    this.drawing = true;
    try {
      // Typing in a text field fires on every keystroke; only the last state matters.
      while (this.pending) {
        const next = this.pending;
        this.pending = undefined;
        if (this.skia) await this.drawSkia(next.scene, next.low);
        else await this.drawCanvas2d(next.scene);
        this.paintedScene = next.scene;
        this.options.onDrawn?.();
        // Rendering is main-thread WASM work. Back-to-back renders while the
        // shopper types would saturate the CPU and make the typing itself
        // stutter, so consecutive renders keep a breather between them — the
        // final state still lands within a beat of the last keystroke.
        if (this.pending) await new Promise((resolve) => setTimeout(resolve, 150));
      }
    } finally {
      this.drawing = false;
    }
  }

  private async drawSkia(scene: Scene, low = false): Promise<void> {
    const canvas = this.options.canvas;
    // Rendered at the device's real pixel density (capped — a 3× phone screen
    // does not need a 3× render), otherwise the preview is visibly soft exactly
    // on the phones most shoppers order from. Draft renders cap much lower:
    // mid-burst the shopper is looking at their fingers, not at edge crispness.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const boxWidth = displayWidth(canvas, scene);
    const target = low ? Math.min(boxWidth, 640) : boxWidth * dpr;
    const scale = Math.min(1, target / scene.width);

    const request = {
      scene,
      scale,
      fonts: this.options.product.fonts.map((f) => ({ id: f.id, url: f.url })),
      // The full asset map, not just the template's: uploads registered since
      // boot have to reach the renderer too.
      assets: [...this.assetUrls].map(([id, url]) => ({ id, url })),
      mapboxToken: this.options.product.mapboxToken,
    };

    // The raw path paints pixels straight into the canvas. The PNG fallback
    // (an older cached renderer script) costs a full encode + decode per frame.
    if (this.skia!.renderRaw) {
      const { pixels, width, height, retryInMs } = await this.skia!.renderRaw(request);
      // A photo that did not arrive (a dropped request on a phone) must not
      // stay missing until the shopper happens to change something.
      window.clearTimeout(this.retryTimer);
      if (retryInMs !== undefined) {
        this.retryTimer = window.setTimeout(() => {
          if (this.lastScene === scene) void this.enqueue(scene, false);
        }, retryInMs + 50);
      }
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) return;
      // Copied out: the renderer's view is backed by WASM memory, which
      // ImageData will not accept directly.
      const bytes = new Uint8ClampedArray(pixels.byteLength);
      bytes.set(pixels);
      context.putImageData(new ImageData(bytes, width, height), 0, 0);
      return;
    }

    const png = await this.skia!.render(request);
    const bytes = new Uint8Array(png.byteLength);
    bytes.set(png);
    const blob = new Blob([bytes.buffer], { type: 'image/png' });
    const picture = await decodeBlob(blob);
    if (!picture) return;
    canvas.width = picture.width;
    canvas.height = picture.height;
    const context = canvas.getContext('2d');
    context?.drawImage(picture.image, 0, 0);
    picture.release();
  }

  /** A plain 2D approximation, used until (or instead of) the print renderer. */
  private async drawCanvas2d(scene: Scene, maxEdge?: number): Promise<void> {
    const canvas = this.options.canvas;
    const context = canvas.getContext('2d');
    if (!context) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const scale = maxEdge
      ? Math.min(1, maxEdge / Math.max(scene.width, scene.height))
      : Math.min(1, (displayWidth(canvas, scene) * dpr) / scene.width);
    canvas.width = Math.round(scene.width * scale);
    canvas.height = Math.round(scene.height * scale);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.save();
    context.scale(scale, scale);

    if (scene.background?.assetId) {
      const image = await this.image(scene.background.assetId);
      if (image) context.drawImage(image, 0, 0, scene.width, scene.height);
    }

    for (const element of [...scene.elements].sort((a, b) => a.z - b.z)) {
      if (element.visible === false || element.printOnly) continue;
      const { cx, cy, w, h, rotation } = element.transform;

      context.save();
      context.translate(cx, cy);
      if (rotation) context.rotate((rotation * Math.PI) / 180);

      if (element.type === 'rect') {
        context.fillStyle = element.fill.hex;
        context.fillRect(-w / 2, -h / 2, w, h);
      } else if (element.type === 'image') {
        // Same resolution order as the print renderer's `resolveImageAsset`:
        // answer, fixed artwork, test picture, first gallery option.
        const sample = element.source.kind !== 'static' ? element.sample : undefined;
        const assetId =
          element.value ??
          (element.source.kind === 'static' ? element.source.assetId : undefined) ??
          sample?.assetId ??
          (element.source.kind === 'choices' ? element.source.assetIds[0] : undefined);
        const image = assetId ? await this.image(assetId) : undefined;
        if (image) {
          // A free crop frames the picture whatever the fit (see the print
          // renderer's drawImage).
          const framing = !element.value && sample?.assetId ? sample.crop : element.crop;
          const fit = framing?.free ? 'cover' : (element.fit ?? 'cover');
          context.save();
          context.beginPath();
          context.rect(-w / 2, -h / 2, w, h);
          context.clip();
          if (fit === 'fill') {
            // Migrated static artwork stretches to the box, like its source.
            context.drawImage(image, 0, 0, image.width, image.height, -w / 2, -h / 2, w, h);
          } else if (fit === 'contain') {
            // The whole picture, centred, nothing stretched — matches the
            // print renderer's letterbox.
            const k = Math.min(w / image.width, h / image.height);
            const dw = image.width * k;
            const dh = image.height * k;
            context.drawImage(image, 0, 0, image.width, image.height, -dw / 2, -dh / 2, dw, dh);
          } else {
            // Cover fit with the shopper's framing — the print renderer's own
            // window (`photoWindow`), including a placed head's free window.
            // The test picture carries its own framing; an answer uses the shopper's.
            const crop = !element.value && sample?.assetId ? sample.crop : element.crop;
            let win = photoWindow(image.width, image.height, w, h, crop);
            let box = { x: -w / 2, y: -h / 2, w, h };
            // "Straighten the picture": the photo rotates inside the fixed box,
            // enlarged to cover the turned box exactly as the print renderer
            // does (`coverPhotoFrame`) — without it this fallback drew a
            // straightened photo smaller than the print.
            if (crop?.rotation) {
              const frame = coverPhotoFrame({ w: image.width, h: image.height }, { w, h }, crop);
              win = frame.source;
              box = { ...frame.destination, x: frame.destination.x - w / 2, y: frame.destination.y - h / 2 };
              context.rotate((crop.rotation * Math.PI) / 180);
            }
            const drawn = crop?.free
              ? clipPhotoWindow(win, box, image.width, image.height, crop.keep)
              : { src: win, dst: box };
            if (drawn) {
              context.drawImage(image, drawn.src.x, drawn.src.y, drawn.src.w, drawn.src.h, drawn.dst.x, drawn.dst.y, drawn.dst.w, drawn.dst.h);
            }
          }
          context.restore();
        } else {
          context.fillStyle = 'rgba(0,0,0,.06)';
          context.fillRect(-w / 2, -h / 2, w, h);
        }
      } else if (element.type === 'text') {
        const raw = element.value ?? element.default ?? '';
        if (raw) {
          // The same string the Skia renderer resolves — the fixed words and
          // symbols included, or the text would visibly re-flow the moment the
          // real renderer takes over. The SHAPE is still flat here: a curved,
          // path or warped layer is drawn as plain text rather than left as an
          // empty slot, and takes its curve when the Skia bundle lands.
          const glued = `${element.prefix ?? ''}${applyTextGlue(raw, element)}${element.suffix ?? ''}`;
          const text = element.uppercase ? glued.toLocaleUpperCase() : glued;
          // The Serbian twin, exactly as the print renderer switches to it.
          const drawnFont = withScriptFallback(element, text).font.fontId;
          const family = this.fontFamilies.get(drawnFont) ?? 'sans-serif';
          if (usesTextFlow(element)) {
            drawFlowFallback(context, element, text, family, w, h);
          } else {
            // Vertical text is a column of single letters, one per line.
            const first = element.vertical ? graphemes(text) : text.split('\n');
            const size = fitText(context, first, element, family, w, h);
            context.font = `${size}px "${family}", sans-serif`;
            // A multiline element WRAPS at the box width — that is what a fixed
            // text box in the designer is — so the lines are only known once the
            // size is. Everything below counts `lines`, so it is done here.
            const lines = element.multiline && !element.vertical ? wrapLines(context, first, w) : first;
            context.textBaseline = 'middle';
            // An unset alignment follows the base direction, as in the print renderer.
            const wanted = element.align ?? (element.direction === 'rtl' ? 'right' : 'left');
            const align = wanted === 'center' ? 'center' : wanted === 'right' ? 'right' : 'left';
            context.textAlign = align;
            // Customily's "Right To Left Text": the base direction the canvas
            // resolves neutral characters against. `left`/`right` above are
            // absolute edges, so the anchor maths below is unchanged; the state
            // is scoped by the save() every element draws inside.
            if (element.direction === 'rtl') context.direction = 'rtl';
            const x = align === 'center' ? 0 : align === 'right' ? w / 2 : -w / 2;
            const lineHeight = size * (element.lineSpacing ?? 1);
            // The anchor: where the block of lines sits in the box. The real
            // renderer aligns on the ink; this one has only the line boxes, which
            // is close enough for a stand-in that lives until Skia loads.
            const block = lines.length * lineHeight;
            const columnTop =
              element.verticalAlign === 'top' ? -h / 2
                : element.verticalAlign === 'bottom' ? h / 2 - block
                  : -block / 2;
            // Only the fills a 2D canvas gives for free are honoured here: a
            // gradient and alternating colours. Patterns, blends and the repeat
            // grid are left to the Skia renderer, which is the truth for all of
            // them — this is a stand-in until it loads, not a second renderer.
            if (element.gradient && element.gradient.stops.length >= 2) {
              // Across the letters, like the print renderer — not the box, which
              // auto-fit usually leaves far larger than the name in it.
              const inkW = Math.max(...lines.map((line) => context.measureText(line).width));
              const inkH = lines.length * lineHeight;
              const inkCx = align === 'center' ? 0 : align === 'right' ? x - inkW / 2 : x + inkW / 2;
              context.fillStyle = textGradient(context, element.gradient, inkCx, 0, inkW, inkH);
            } else {
              context.fillStyle = element.color.hex;
            }
            strokeTextFallback(context, element, lines.map((text, row) => ({
              text, x, y: columnTop + (row + 0.5) * lineHeight,
            })));
            lines.forEach((line, row) => {
              const y = columnTop + (row + 0.5) * lineHeight;
              if (element.alternateColors?.length) {
                fillAlternating(context, line, x, y, align, element.alternateColors, element.color.hex);
              } else {
                context.fillText(line, x, y);
              }
            });
          }
        }
      } else if (element.type === 'qr') {
        // The real code is generated at commit; a placeholder keeps the layout honest.
        context.fillStyle = '#111';
        context.fillRect(-w / 2, -h / 2, w, h);
        context.fillStyle = '#fff';
        context.fillRect(-w / 2 + w * 0.1, -h / 2 + h * 0.1, w * 0.8, h * 0.8);
        context.fillStyle = '#111';
        context.font = `${Math.max(8, w * 0.12)}px sans-serif`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText('QR', 0, 0);
      }
      context.restore();
    }
    context.restore();
  }

  private async image(assetIdOrUrl: string): Promise<HTMLImageElement | undefined> {
    const cached = this.images.get(assetIdOrUrl);
    if (cached) return cached;
    const url = this.assetUrls.get(assetIdOrUrl) ?? assetIdOrUrl;
    if (!/^https?:|^data:|^blob:/i.test(url)) return undefined;

    return new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        // An older upload may finish loading after its AI result replaced it.
        // It must not put the original pixels back into the shared-id cache.
        if ((this.assetUrls.get(assetIdOrUrl) ?? assetIdOrUrl) === url) this.images.set(assetIdOrUrl, image);
        resolve(image);
      };
      image.onerror = () => resolve(undefined);
      image.src = url;
    });
  }

  /**
   * The collage board's pictures ("Raspored slika"): the scene — whole, or only
   * `viewport` (a rectangle in scene px) — at `scale`, as raw RGBA, beside the
   * preview rather than into it: the live canvas, its queue and `paintedScene`
   * are not touched. Undefined without the print renderer (the flat fallback
   * cannot draw a part), and for a viewport when the renderer bundle is an
   * older cached one that would draw the whole scene instead (the board then
   * just shows a softer zoom). A board opened while the renderer is still on
   * its way waits for it a little, as `headWindow` does — while `waiting`
   * says one is (the page knows: a 2 GB phone never loads it, and a failed
   * load is not coming), so the board is not kept on "Pripremam raspored…"
   * for a renderer that will never arrive.
   */
  async renderView(
    scene: Scene,
    options: { scale: number; viewport?: { x: number; y: number; w: number; h: number }; waiting?: () => boolean },
  ): Promise<{ pixels: Uint8Array; width: number; height: number } | undefined> {
    const coming = options.waiting ?? (() => true);
    for (let waited = 0; !this.skia && coming() && waited < Preview.renderViewWaitMs; waited += 200) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    const bundle = this.skia as (RenderBundle & { features?: { viewport?: boolean } }) | undefined;
    if (!bundle?.renderRaw) return undefined;
    if (options.viewport && !bundle.features?.viewport) return undefined;
    const request: RenderRequest & { viewport?: { x: number; y: number; w: number; h: number } } = {
      scene: this.withTwins(scene),
      scale: options.scale,
      fonts: this.options.product.fonts.map((f) => ({ id: f.id, url: f.url })),
      assets: [...this.assetUrls].map(([id, url]) => ({ id, url })),
      mapboxToken: this.options.product.mapboxToken,
      ...(options.viewport ? { viewport: { ...options.viewport } } : {}),
    };
    const { pixels, width, height } = await bundle.renderRaw(request);
    return { pixels, width, height };
  }

  /** Whether `renderView` can draw a part of a scene (a renderer bundle with `features.viewport`). */
  get supportsViewport(): boolean {
    const bundle = this.skia as (RenderBundle & { features?: { viewport?: boolean } }) | undefined;
    return Boolean(bundle?.renderRaw && bundle.features?.viewport);
  }

  /** How long `renderView` waits for a renderer still on its way (a board opened right after the page). */
  static renderViewWaitMs = 5000;
}

type TextEl = Extract<SceneElement, { type: 'text' }>;

/**
 * A text the print renderer lays out with the shared flow (text-flow.ts) —
 * a multiline auto-fit text, or one limited to a shape — drawn here with the
 * same line breaks, measured by this canvas. Wrapping while big, the box cut
 * by a heart: a stand-in that broke lines anywhere else would re-flow the
 * moment the Skia renderer takes over.
 */
function drawFlowFallback(
  context: CanvasRenderingContext2D,
  element: TextEl,
  content: string,
  family: string,
  w: number,
  h: number,
): void {
  const text = element.vertical ? graphemes(content).join('\n') : content;
  const at100 = `100px "${family}", sans-serif`;
  context.font = at100;
  const probe = context.measureText('Hg');
  const ascent = (probe.fontBoundingBoxAscent ?? probe.actualBoundingBoxAscent ?? 80) / 100;
  const descent = (probe.fontBoundingBoxDescent ?? probe.actualBoundingBoxDescent ?? 20) / 100;
  const natural = ascent + descent || 1;
  // Line spacing is the paragraph engine's height multiplier: the line box
  // becomes that many ems, shared between ascent and descent.
  const k = element.lineSpacing ? element.lineSpacing / natural : 1;
  const font: TextFlowFont = {
    advance: (s) => {
      context.font = at100;
      return context.measureText(s).width / 100;
    },
    lineAscent: ascent * k,
    lineDescent: descent * k,
    lineStep: natural * k,
    inkAscent: ascent,
    inkDescent: descent,
  };
  const wanted = element.align ?? (element.direction === 'rtl' ? 'right' : 'left');
  const range = textFitRange(element);
  const flow = flowText({
    text,
    width: w,
    height: h,
    region: element.fitShape ? textFlowRegion(element.fitShape, w, h) : null,
    minSize: range.min,
    maxSize: range.max,
    wrap: Boolean(element.multiline || element.vertical),
    maxLines: element.maxLines,
    align: wanted,
    verticalAlign: element.verticalAlign ?? 'center',
    font,
  });
  context.font = `${flow.size}px "${family}", sans-serif`;
  context.textBaseline = 'alphabetic';
  context.textAlign = 'left';
  if (element.direction === 'rtl') context.direction = 'rtl';
  const drawn = flow.lines.filter((line) => line.text !== '');
  if (element.gradient && element.gradient.stops.length >= 2 && drawn.length) {
    const left = Math.min(...drawn.map((l) => l.x));
    const right = Math.max(...drawn.map((l) => l.x + l.width));
    const top = drawn[0]!.baseline - ascent * flow.size;
    const bottom = drawn[drawn.length - 1]!.baseline + descent * flow.size;
    context.fillStyle = textGradient(context, element.gradient, (left + right) / 2 - w / 2, (top + bottom) / 2 - h / 2, right - left, bottom - top);
  } else {
    context.fillStyle = element.color.hex;
  }
  strokeTextFallback(context, element, drawn.map((line) => ({
    text: line.text, x: line.x - w / 2, y: line.baseline - h / 2,
  })));
  for (const line of drawn) {
    const x = line.x - w / 2;
    const y = line.baseline - h / 2;
    if (element.alternateColors?.length) {
      fillAlternating(context, line.text, x, y, 'left', element.alternateColors, element.color.hex);
    } else {
      context.fillText(line.text, x, y);
    }
  }
}

/** The same scene-pixel stroke as print, underneath every line's fill. */
function strokeTextFallback(
  context: CanvasRenderingContext2D,
  element: TextEl,
  lines: Array<{ text: string; x: number; y: number }>,
): void {
  if (!element.outline || !(element.outline.width > 0)) return;
  context.strokeStyle = cssColor(element.outline.color);
  context.lineWidth = element.outline.width;
  for (const line of lines) context.strokeText(line.text, line.x, line.y);
}

/** User-perceived characters, so an accent stays with its letter in a column. */
function graphemes(text: string): string[] {
  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    return Array.from(segmenter.segment(text), (s) => s.segment);
  }
  return [...text];
}

/**
 * Shrinks text until every line fits the box, mirroring the print renderer's
 * auto-fit. A column of letters is bounded by height first: its width only
 * ever needs one wide glyph to fit.
 */
function fitText(
  context: CanvasRenderingContext2D,
  lines: string[],
  element: TextEl,
  family: string,
  boxWidth: number,
  boxHeight: number,
): number {
  const max = element.font.maxSize ?? element.font.size ?? Math.min(boxHeight, 200);
  const min = element.font.minSize && element.font.minSize > 0 ? element.font.minSize : 6;
  const lineHeight = element.lineSpacing ?? 1;
  let size = lines.length > 1 ? Math.max(min, Math.min(max, boxHeight / (lines.length * lineHeight))) : max;
  for (let i = 0; i < 12 && size > min; i++) {
    context.font = `${size}px "${family}", sans-serif`;
    if (lines.every((line) => context.measureText(line).width <= boxWidth)) break;
    size = Math.max(min, size * 0.9);
  }
  return size;
}

/**
 * Greedy word wrap at the box width, in the font the context already carries.
 *
 * The print renderer wraps a multiline element at exactly the element width, so
 * this stand-in has to as well: a fixed text box in the designer is a box whose
 * width decides where the lines break, and a preview that puts the break
 * somewhere else is showing the shopper a different design. A word longer than
 * the box is broken by character, the way Skia breaks it.
 */
function wrapLines(context: CanvasRenderingContext2D, paragraphs: string[], boxWidth: number): string[] {
  if (!(boxWidth > 0)) return paragraphs;
  const out: string[] = [];
  for (const paragraph of paragraphs) {
    const started = out.length;
    let line = '';
    for (const word of paragraph.split(/(\s+)/)) {
      if (!word) continue;
      const candidate = line + word;
      if (line && context.measureText(candidate).width > boxWidth) {
        out.push(line);
        line = '';
        if (/^\s+$/.test(word)) continue; // a break eats the space it broke at
        line = word;
      } else {
        line = candidate;
      }
      // A single word wider than the box: break it where it runs out of room.
      while (context.measureText(line).width > boxWidth && [...line].length > 1) {
        const letters = [...line];
        let fit = letters.length - 1;
        while (fit > 1 && context.measureText(letters.slice(0, fit).join('')).width > boxWidth) fit -= 1;
        out.push(letters.slice(0, fit).join(''));
        line = letters.slice(fit).join('');
      }
    }
    // The last line of the paragraph — and an empty paragraph is still a line,
    // because a shopper who typed a blank line meant to leave a gap.
    if (line || out.length === started) out.push(line);
  }
  return out.length ? out : paragraphs;
}

const cssColor = (color: TextEl['color']): string => {
  const alpha = color.alpha ?? 1;
  if (alpha >= 1) return color.hex;
  const m = /^#?([0-9a-f]{6})$/i.exec(color.hex);
  if (!m) return color.hex;
  const int = parseInt(m[1]!, 16);
  return `rgba(${(int >> 16) & 255},${(int >> 8) & 255},${int & 255},${alpha})`;
};

/**
 * The print renderer's gradient over an ink rectangle centred at (cx, cy) in
 * the element's own frame: 0° runs left to right, 90° top to bottom, and the
 * run spans the ink's projection onto that direction.
 */
function textGradient(
  context: CanvasRenderingContext2D,
  gradient: NonNullable<TextEl['gradient']>,
  cx: number,
  cy: number,
  w: number,
  h: number,
): CanvasGradient {
  const rad = (gradient.angle * Math.PI) / 180;
  const dx = Math.cos(rad);
  const dy = Math.sin(rad);
  const half = Math.max(1, (Math.abs(dx) * w + Math.abs(dy) * h) / 2);
  const fill = context.createLinearGradient(
    cx - dx * half,
    cy - dy * half,
    cx + dx * half,
    cy + dy * half,
  );
  for (const stop of [...gradient.stops].sort((a, b) => a.offset - b.offset)) {
    fill.addColorStop(Math.min(1, Math.max(0, stop.offset)), cssColor(stop.color));
  }
  return fill;
}

/**
 * One line letter by letter, cycling the colours over the letters and leaving
 * blanks out of the cycle — the same rule the print renderer follows. Measured
 * per letter, so kerning pairs sit a hair apart from the real render; close
 * enough to choose colours by.
 */
function fillAlternating(
  context: CanvasRenderingContext2D,
  line: string,
  x: number,
  y: number,
  align: 'left' | 'center' | 'right',
  colors: TextEl['alternateColors'] & object,
  base: string,
): void {
  const letters = graphemes(line);
  // The colours cycle over the letters in READING order; a right-to-left
  // line is then laid down from its last letter to its first, the same
  // whole-string reversal the print renderer's per-glyph painters use.
  let k = 0;
  const fills = letters.map((letter) =>
    /^\s+$/u.test(letter) ? base : cssColor(colors[k++ % colors.length]!),
  );
  if (context.direction === 'rtl') {
    letters.reverse();
    fills.reverse();
  }
  const widths = letters.map((g) => context.measureText(g).width);
  const total = widths.reduce((a, b) => a + b, 0);
  let cursor = align === 'center' ? x - total / 2 : align === 'right' ? x - total : x;
  context.save();
  context.textAlign = 'left';
  letters.forEach((letter, i) => {
    context.fillStyle = fills[i]!;
    context.fillText(letter, cursor, y);
    cursor += widths[i]!;
  });
  context.restore();
}
